interface StorageNode {
  created?: number;
  lastModify?: number;
  data: any;
}

/**
 * A small library that helps to easily manage the data in the localStorage or sessionStorage
 */
export class StorageManager {
  constructor() {}

  /**
   * Saves the given object or array into the specified storage by using the key
   * @param storageType
   * @param key
   * @param data
   */
  private saveData(storageType: 'local' | 'session', key: string, data: Object | Array<any>) {
    let storage = storageType === 'local' ? localStorage : sessionStorage;
    let dataToSave = this.getData(storageType, key);

    if (dataToSave?.created && dataToSave?.lastModify) {
      // There is no data with the given key in the storage. So create one.
      dataToSave.lastModify = new Date().getTime();
      dataToSave.data = data;
    } else {
      // There is already data with the given key in the storage. So modify it.
      dataToSave = {
        created: new Date().getTime(),
        lastModify: new Date().getTime(),
        data: data
      };
    }

    // Save the result.
    storage.setItem(key, JSON.stringify(dataToSave));
  }

  /**
   * Return the data in the specified storage by using the key
   */
  private getData(storageType: 'local' | 'session', key: string): StorageNode {
    let storage = storageType === 'local' ? localStorage : sessionStorage;
    let data = JSON.parse(storage.getItem(key));

    if (!data) {
      // There is no data at all.
      return undefined;
    } else if (data?.created && data?.lastModify) {
      // This is an StorageNode object. Return it.
      return data;
    } else {
      // This is not a standardized StorageNode object. Return it in a StorageNode.
      return {
        created: undefined,
        lastModify: undefined,
        data: data
      };
    }
  }

  /**
   * Saves the data into the localStorage
   * @param key
   * @param data
   */
  public setLocalObject(key: string, data: Object | Array<any>) {
    this.saveData('local', key, data);
  }

  /**
   * Gets the data with the given key from the localStorage
   * @param key
   */
  public getLocalObject(key: string): StorageNode {
    return this.getData('local', key);
  }

  /**
   * Removes the data with the given key from the localStorage
   * @param key
   */
  public removeLocalObject(key: string) {
    localStorage.removeItem(key);
  }

  /**
   * Saves the data into the sessionStorage
   * @param key
   * @param data
   */
  public setSessionObject(key: string, data: Object | Array<any>) {
    this.saveData('session', key, data);
  }

  /**
   * Gets the data with the given key from the sessionStorage
   * @param key
   */
  public getSessionObject(key: string): StorageNode {
    return this.getData('session', key);
  }

  /**
   * Removes the data with the given key from the sessionStorage
   * @param key
   */
  public removeSessionObject(key: string) {
    sessionStorage.removeItem(key);
  }

  /**
   * Searches the localStorage and the sessionStorage accordingly. If a data with the key is found, returns it.
   * @param key
   */
  public getCommonObject(key: string): StorageNode {
    let local_item = localStorage.getItem(key);

    if (local_item) {
      return this.getLocalObject(key);
    }

    let session_item = sessionStorage.getItem(key);

    if (session_item) {
      return this.getSessionObject(key);
    }

    return undefined;
  }

  /**
   * Removes the data with the given key from all storages
   * @param key
   */
  public removeCommonObject(key: string) {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  }
}
